import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenNSReport from "./HiddenNSReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A80D2C",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
orange: "#F97316" ,
};

// ─── PER-STYLE ACCENT COLOURS ─────────────────────────────────────────────────
const STYLE_COLOR = {
  CO: T.navy,
  CL: "#1A7A4A",
  CM: T.gold,
  AC: "#5B4A8A",
  AV: T.orange,
};

// ─── NEGOTIATING STYLE DEFINITIONS ───────────────────────────────────────────
const NS_DEFINITIONS = [
  {
    code: "CO", key: "competing", label: "Competing",
    tagline: '"I focus on the bottom line and expect to win."',
    assertiveness: "High", cooperativeness: "Low", bargaining: "Distributive Bargaining",
    overview: "Negotiators with this style are results-oriented, self-confident, and assertive. They are focused primarily on the bottom line, tend to impose their views on the other party, and in the extreme can become aggressive and domineering.",
    orgNote: "Competing is effective when quick, decisive action is needed or when you must stand firm on non-negotiable principles. Overuse, however, can damage relationships and reduce the quality of long-term outcomes. Pairing assertiveness with a degree of openness produces better results in recurring negotiations.",
    toDo: [
      "Recognise that winning every battle can mean losing the war — long-term relationships matter.",
      "Practise listening actively to the other party's position before staking your own.",
      "Reserve a hard-competing stance for situations where the outcome is truly non-negotiable.",
      "Explore whether Collaborating can achieve the same result while preserving the relationship.",
    ],
  },
  {
    code: "CL", key: "collaborating", label: "Collaborating",
    tagline: '"Let\'s find a solution that works for both of us."',
    assertiveness: "High", cooperativeness: "High", bargaining: "Mutual Gain Bargaining",
    overview: "Negotiators with this style use open and honest communication, focus on finding creative solutions that mutually satisfy both parties, are open to exploring new and novel solutions, and suggest many alternatives for consideration.",
    orgNote: "Collaborating is the gold standard for complex, long-term negotiations where relationship quality matters as much as the outcome. It requires trust and transparency from both parties. When the other side is not forthcoming, collaborators must be careful not to be exploited.",
    toDo: [
      "Continue investing in relationship-building before and during negotiations.",
      "Prepare thoroughly — understanding the other party's interests is the foundation of joint solutions.",
      "Be alert to situations where the other party is not collaborating in good faith.",
      "Share information openly, but verify that reciprocal transparency is present.",
    ],
  },
  {
    code: "CM", key: "compromising", label: "Compromising",
    tagline: '"Let\'s meet halfway and both walk away satisfied."',
    assertiveness: "Moderate", cooperativeness: "Moderate", bargaining: "Mixed",
    overview: "Negotiators with this style aim to find the middle ground, often split the difference between positions, frequently engage in give-and-take tradeoffs, and accept moderate satisfaction of both parties' needs.",
    orgNote: "Compromising is pragmatic and time-efficient. It works well when both parties have equal power and a quick resolution is preferable to a prolonged negotiation. It may, however, leave value on the table that a collaborative approach would have captured.",
    toDo: [
      "Ask whether splitting the difference is truly optimal or simply expedient.",
      "Explore whether a creative trade on different issues could yield more for both sides.",
      "Use compromising as a fallback when collaboration stalls, not as a first resort.",
      "Track the pattern — if you always compromise, the other party may anchor aggressively.",
    ],
  },
  {
    code: "AC", key: "accommodating", label: "Accommodating",
    tagline: '"I value the relationship above my own outcome."',
    assertiveness: "Low", cooperativeness: "High", bargaining: "Mutual Gain Bargaining",
    overview: "Negotiators with this style make attempts to maintain relationships with the other party, smooth over conflicts, downplay differences, and are most concerned with satisfying the needs of the other party.",
    orgNote: "Accommodating builds goodwill and can preserve important relationships, particularly when the issue at hand matters more to the other party. Chronically accommodating, however, leads to cumulative disadvantage and can signal weakness to strategic negotiators.",
    toDo: [
      "Distinguish between genuine accommodation (strategic relationship investment) and habitual yielding.",
      "Assert your core interests clearly before conceding on secondary points.",
      "Practise saying 'I need to think about that' rather than agreeing immediately.",
      "Balance relationship focus with outcome focus — your needs matter too.",
    ],
  },
  {
    code: "AV", key: "avoiding", label: "Avoiding",
    tagline: '"I prefer to step back rather than engage in conflict."',
    assertiveness: "Low", cooperativeness: "Low", bargaining: "Neither",
    overview: "Negotiators with this style are passive, prefer to avoid conflict, make attempts to withdraw from the situation or pass responsibility onto another party, and fail to show adequate concern or make an honest attempt to get to a solution.",
    orgNote: "Avoidance has a narrow legitimate application — deferring a trivial issue or a highly charged moment when emotions need to cool. Systematic avoidance, however, means unresolved issues accumulate and others fill the negotiating vacuum, often at your expense.",
    toDo: [
      "Identify which negotiations you are avoiding and ask what the cost of continued avoidance is.",
      "Develop a simple opening script to step into difficult conversations with calm authority.",
      "Distinguish between strategic timing (deferring wisely) and fear-driven withdrawal.",
      "Seek low-stakes practice negotiations to build comfort and confidence.",
    ],
  },
];

// ─── BAND THRESHOLDS ──────────────────────────────────────────────────────────
const STYLE_BANDS = {
  CO: { high: 18, mod_to_high: 16, mod_to_low: 14, max: 25 },
  AV: { high: 18, mod_to_high: 16, mod_to_low: 14, max: 25 },
  CL: { high: 21, mod_to_high: 19, mod_to_low: 17, max: 25 },
  AC: { high: 19, mod_to_high: 17, mod_to_low: 15, max: 25 },
  CM: { high: 20, mod_to_high: 18, mod_to_low: 16, max: 25 },
};

const BAND_RANGES = {
  CO: { High: "18 or above", "Moderate to High": "16–17", "Moderate to Low": "14–15", Low: "13 or below" },
  AV: { High: "18 or above", "Moderate to High": "16–17", "Moderate to Low": "14–15", Low: "13 or below" },
  CL: { High: "21 or above", "Moderate to High": "19–20", "Moderate to Low": "17–18", Low: "16 or below" },
  AC: { High: "19 or above", "Moderate to High": "17–18", "Moderate to Low": "15–16", Low: "14 or below" },
  CM: { High: "20 or above", "Moderate to High": "18–19", "Moderate to Low": "16–17", Low: "15 or below" },
};

const BAND_DESCRIPTIONS = {
  CO: {
    High: "You strongly exhibit Competing characteristics — results-oriented, self-confident, assertive, and focused on the bottom line. In the extreme this can appear aggressive. You are most aligned with Distributive Bargaining.",
    "Moderate to High": "You moderately to strongly exhibit Competing characteristics. You advocate firmly for your position while retaining some openness to the other party.",
    "Moderate to Low": "You exhibit mild Competing characteristics. You can assert yourself when necessary but generally prefer a less confrontational stance.",
    Low: "You only weakly exhibit Competing characteristics. You are unlikely to use aggressive tactics or hard-positional bargaining.",
  },
  CL: {
    High: "You strongly exhibit Collaborating characteristics — open communication, creativity, and a drive for joint solutions that satisfy both parties. This is the gold standard for Mutual Gain negotiations.",
    "Moderate to High": "You moderately to strongly exhibit Collaborating characteristics. You seek win-win outcomes and invest in understanding the other party's interests.",
    "Moderate to Low": "You exhibit mild Collaborating characteristics. You are open to joint problem-solving but may default to other styles under pressure.",
    Low: "You only weakly exhibit Collaborating characteristics. You tend to approach negotiations as more distributive rather than joint problem-solving.",
  },
  CM: {
    High: "You strongly exhibit Compromising characteristics — you naturally seek the middle ground, engage in give-and-take, and accept partial satisfaction as a fair outcome.",
    "Moderate to High": "You moderately to strongly exhibit Compromising characteristics. You are pragmatic and tend to resolve deadlocks efficiently through mutual concession.",
    "Moderate to Low": "You exhibit mild Compromising characteristics. You will split the difference when necessary but prefer to hold out for a better outcome.",
    Low: "You only weakly exhibit Compromising characteristics. You tend to pursue full satisfaction rather than splitting the difference.",
  },
  AC: {
    High: "You strongly exhibit Accommodating characteristics — you prioritise the relationship, smooth over conflict, and are most concerned with satisfying the other party's needs.",
    "Moderate to High": "You moderately to strongly exhibit Accommodating characteristics. You are relationship-focused and generous in concessions, especially when you value the other party.",
    "Moderate to Low": "You exhibit mild Accommodating characteristics. You balance relationship maintenance with the pursuit of your own interests.",
    Low: "You only weakly exhibit Accommodating characteristics. You rarely yield for relationship reasons alone.",
  },
  AV: {
    High: "You strongly exhibit Avoiding characteristics — you prefer to sidestep conflict, withdraw from negotiations, or pass responsibility rather than engage directly.",
    "Moderate to High": "You moderately to strongly exhibit Avoiding characteristics. You may defer or delay negotiations and prefer others take the lead in conflict situations.",
    "Moderate to Low": "You exhibit mild Avoiding characteristics. You occasionally step back from negotiations but can engage when necessary.",
    Low: "You only weakly exhibit Avoiding characteristics. You are generally comfortable engaging in negotiations and conflict situations.",
  },
};

function getStyleBandLabel(code, score) {
  const b = STYLE_BANDS[code];
  if (!b || score == null) return "—";
  if (score >= b.high) return "High";
  if (score >= b.mod_to_high) return "Moderate to High";
  if (score >= b.mod_to_low) return "Moderate to Low";
  return "Low";
}

// ─── DATE FORMATTER ───────────────────────────────────────────────────────────
function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────────
function Skeleton({ h = 24, w = "100%", r = 8 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: "linear-gradient(90deg,#EAF0F6 25%,#f5f8fc 50%,#EAF0F6 75%)",
      backgroundSize: "200% 100%",
      animation: "ns-shimmer 1.4s infinite",
    }} />
  );
}

function Callout({ children }) {
  return (
    <div style={{
      background: "linear-gradient(180deg,#fff9e8,#fffdf2)",
      border: "1px solid #F2D58A",
      borderRadius: 16,
      padding: "14px 16px",
      fontSize: "var(--ns-text-sm)",
      lineHeight: 1.6,
      color: T.ink,
      textAlign: "justify",
    }}>
      {children}
    </div>
  );
}

// ─── PENTAGON RADAR CHART ─────────────────────────────────────────────────────
function RadarChart({ scores, dominantStyles, loading }) {
  const cx = 150, cy = 155, maxR = 98;
  const order = ["CL", "CO", "CM", "AV", "AC"];
  const colors = order.map(k => STYLE_COLOR[k]);
  const MAX_SCORE = 25;
  const n = order.length;
  function angle(i) { return (i * 360) / n - 90; }
  function polar(deg, r) {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }
  const rings = [5, 10, 15, 20, 25];
  const vertices = order.map((_, i) => polar(angle(i), maxR));
  const scorePoints = order.map((code, i) => {
    const r = ((scores?.[code] ?? 0) / MAX_SCORE) * maxR;
    return polar(angle(i), Math.max(r, 2));
  });
  const polyStr = scorePoints.map(p => `${p.x},${p.y}`).join(" ");

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <svg viewBox="0 0 300 310" width="100%" style={{ maxWidth: 300 }}>
        {rings.map(v => {
          const pts = order.map((_, i) => polar(angle(i), (v / MAX_SCORE) * maxR));
          return <polygon key={v} points={pts.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#DCE5EF" strokeWidth={1} />;
        })}
        {rings.map(v => {
          const pt = polar(-90, (v / MAX_SCORE) * maxR);
          return <text key={`rl-${v}`} x={pt.x + 3} y={pt.y} fontSize="7" fill={T.slate} dominantBaseline="middle">{v}</text>;
        })}
        {vertices.map((pt, i) => (
          <line key={i} x1={cx} y1={cy} x2={pt.x} y2={pt.y} stroke="#DCE5EF" strokeWidth={1} />
        ))}
        {!loading && (
          <polygon points={polyStr} fill={`${T.navy}22`} stroke={T.navy} strokeWidth={2} strokeLinejoin="round"
            style={{ transition: "all .8s cubic-bezier(.4,0,.2,1)" }} />
        )}
        {!loading && scorePoints.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={5}
            fill={dominantStyles?.includes(order[i]) ? colors[i] : "#fff"}
            stroke={colors[i]} strokeWidth={2} />
        ))}
        {order.map((code, i) => {
          const LABEL_R = maxR + 24;
          const pt = polar(angle(i), LABEL_R);
          const isDom = dominantStyles?.includes(code);
          return (
            <g key={code}>
              <text x={pt.x} y={pt.y - 6} textAnchor="middle" dominantBaseline="middle"
                fontSize="11" fontWeight="700" fill={isDom ? STYLE_COLOR[code] : T.ink}>{code}</text>
              <text x={pt.x} y={pt.y + 9} textAnchor="middle" dominantBaseline="middle"
                fontSize="8.5" fill={T.slate}>{NS_DEFINITIONS.find(d => d.code === code)?.label}</text>
            </g>
          );
        })}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="9" fill={T.slate} fontWeight={600} letterSpacing="1">STYLE</text>
        <text x={cx} y={cy + 8} textAnchor="middle" fontSize="9" fill={T.slate} fontWeight={600} letterSpacing="1">PROFILE</text>
      </svg>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
        {order.map((code, i) => (
          <div key={code} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--ns-text-xs)" }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: colors[i] }} />
            <span style={{ color: T.ink }}>{code}: <strong>{loading ? "—" : (scores?.[code] ?? "—")}</strong></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 2D ASSERTIVENESS / COOPERATIVENESS GRAPH ─────────────────────────────────
// Mirrors the Figure 1 diagram from the self-assessment: both axes are split into
// 4 qualitative bands (Low / Moderate to Low / Moderate to High / High) with no
// numbers shown — matching the official interpretation-guide bandings exactly.
function TwoDGraphSVG({ assertivenessIndex, cooperativenessIndex }) {
  const W = 900, H = 680;
  const PAD = { top: 48, right: 48, bottom: 82, left: 138 };
  const GW = W - PAD.left - PAD.right;
  const GH = H - PAD.top - PAD.bottom;

  const BANDS = ["Low", "Moderate to Low", "Moderate to High", "High"];

  // Breakpoints map the raw index onto a continuous 0–4 scale across the 4 bands,
  // using the exact cut-offs from the official interpretation tables:
  //   Assertiveness:   Low ≤ -3 | Mod-to-Low -2..0 | Mod-to-High 1..4 | High ≥ 5
  //   Cooperativeness: Low ≤ 1  | Mod-to-Low 2..4  | Mod-to-High 5..8 | High ≥ 9
  const assertBreaks = [-8, -2.5, 0.5, 4.5, 9];
  const coopBreaks = [-3, 1.5, 4.5, 8.5, 13];

  const bandPosition = (value, breaks) => {
    const v = value ?? (breaks[2] + breaks[1]) / 2;
    const clamped = Math.max(breaks[0], Math.min(breaks[4], v));
    for (let i = 0; i < 4; i++) {
      if (clamped <= breaks[i + 1] || i === 3) {
        const segStart = breaks[i], segEnd = breaks[i + 1];
        const frac = segEnd === segStart ? 0.5 : (clamped - segStart) / (segEnd - segStart);
        return i + frac;
      }
    }
    return 2;
  };

  const assertPos = bandPosition(assertivenessIndex, assertBreaks); // 0 (Low) → 4 (High)
  const coopPos = bandPosition(cooperativenessIndex, coopBreaks);

  const cx = PAD.left + (coopPos / 4) * GW;
  const cy = PAD.top + (1 - assertPos / 4) * GH;
  const axisX = PAD.left + 0.5 * GW; // Low/High midline
  const axisY = PAD.top + 0.5 * GH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto"
      style={{ display: "block", fontFamily: "Arial, sans-serif" }}>
      <rect x={PAD.left} y={PAD.top} width={axisX - PAD.left} height={axisY - PAD.top} fill="rgba(239,68,68,0.07)" />
      <rect x={axisX} y={PAD.top} width={W - PAD.right - axisX} height={axisY - PAD.top} fill="rgba(34,197,94,0.07)" />
      <rect x={PAD.left} y={axisY} width={axisX - PAD.left} height={H - PAD.bottom - axisY} fill="rgba(100,116,139,0.07)" />
      <rect x={axisX} y={axisY} width={W - PAD.right - axisX} height={H - PAD.bottom - axisY} fill="rgba(59,130,246,0.07)" />
      <rect x={PAD.left} y={PAD.top} width={GW} height={GH} fill="none" stroke="#DCE5EF" strokeWidth={1} />
      {[1, 2, 3].map(i => {
        const gx = PAD.left + (i / 4) * GW;
        const gy = PAD.top + (i / 4) * GH;
        return (
          <g key={i}>
            {i !== 2 && <line x1={gx} y1={PAD.top} x2={gx} y2={H - PAD.bottom} stroke="rgba(148,163,184,0.25)" strokeWidth={1} />}
            {i !== 2 && <line x1={PAD.left} y1={gy} x2={W - PAD.right} y2={gy} stroke="rgba(148,163,184,0.25)" strokeWidth={1} />}
          </g>
        );
      })}
      <rect x={PAD.left + 0.25 * GW} y={PAD.top + 0.25 * GH} width={0.5 * GW} height={0.5 * GH}
        fill="rgba(234,179,8,0.10)" stroke="rgba(202,138,4,0.6)" strokeWidth={2} strokeDasharray="6 4" />
      <line x1={axisX} y1={PAD.top} x2={axisX} y2={H - PAD.bottom} stroke="#94a3b8" strokeWidth={2} />
      <line x1={PAD.left} y1={axisY} x2={W - PAD.right} y2={axisY} stroke="#94a3b8" strokeWidth={2} />
      {BANDS.map((label, i) => {
        const gy = PAD.top + (1 - (i + 0.5) / 4) * GH;
        return <text key={`y-${label}`} x={PAD.left - 10} y={gy} fontSize="10.5" fill="#475569" fontWeight="700" textAnchor="end" dominantBaseline="middle">{label}</text>;
      })}
      {BANDS.map((label, i) => {
        const gx = PAD.left + ((i + 0.5) / 4) * GW;
        return <text key={`x-${label}`} x={gx} y={H - PAD.bottom + 20} fontSize="10.5" fill="#475569" fontWeight="700" textAnchor="middle">{label}</text>;
      })}
      <text x={PAD.left + 18} y={PAD.top + 36} fontSize="16" fontWeight="800" fill="#dc2626">Competing</text>
      <text x={W - PAD.right - 18} y={PAD.top + 36} fontSize="16" fontWeight="800" fill="#16a34a" textAnchor="end">Collaborating</text>
      <text x={PAD.left + 18} y={H - PAD.bottom - 28} fontSize="16" fontWeight="800" fill="#64748b">Avoiding</text>
      <text x={W - PAD.right - 18} y={H - PAD.bottom - 28} fontSize="16" fontWeight="800" fill="#2563eb" textAnchor="end">Accommodating</text>
      <rect x={axisX - 64} y={axisY - 16} width={130} height={32} rx={16} fill="#ffffff" stroke="rgba(202,138,4,0.4)" strokeWidth={1.5} />
      <text x={axisX + 1} y={axisY + 5} fontSize="13" fontWeight="800" fill="#ca8a04" textAnchor="middle">Compromising</text>
      <text x={axisX} y={H - 40} fontSize="13" fontWeight="800" fill={T.ink} textAnchor="middle">Cooperativeness</text>
      <text x={axisX} y={H - 18} fontSize="12" fontStyle="italic" fontWeight="600" fill={T.ink} textAnchor="middle">Mutual Gain Bargaining</text>
      <text x={14} y={H / 2} fontSize="13" fontWeight="800" fill={T.ink} textAnchor="middle" transform={`rotate(-90, 14, ${H / 2})`}>Assertiveness</text>
      <text x={32} y={H / 2} fontSize="12" fontStyle="italic" fontWeight="600" fill={T.ink} textAnchor="middle" transform={`rotate(-90, 32, ${H / 2})`}>Distributive Bargaining</text>

      <circle cx={cx} cy={cy} r={14} fill="#7c3aed" stroke="white" strokeWidth={4}
        style={{ filter: "drop-shadow(0 4px 8px rgba(124,58,237,0.4))" }} />
      <rect x={cx - 58} y={cy + 20} width={118} height={30} rx={10} fill="#111827" />
      <text x={cx + 1} y={cy + 40} fontSize="12" fontWeight="600" fill="white" textAnchor="middle">Your Position</text>
    </svg>
  );
}

// ─── INDEX CARD ───────────────────────────────────────────────────────────────
function IndexCard({ title, index, bandLabel, formula, tendency, loading }) {
  const isPositive = (index ?? 0) >= 0;
  return (
    <div style={{
      background: isPositive ? "linear-gradient(180deg,#f0fff4,#e8fdf0)" : "linear-gradient(180deg,#fff5f0,#fdeaea)",
      border: `1px solid ${isPositive ? "#86efac" : "#fca5a5"}`,
      borderRadius: 16, padding: "18px 20px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "var(--ns-text-sm)", color: T.ink, marginBottom: 2 }}>{title}</div>
          <div style={{ fontSize: "var(--ns-text-xs)", color: T.slate }}>{formula}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          {loading ? <Skeleton h={36} w={60} r={8} /> : (
            <div style={{ fontSize: "2rem", fontWeight: 700, color: isPositive ? "#16a34a" : "#dc2626" }}>
              {index != null ? (index > 0 ? `+${index}` : index) : "—"}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ background: isPositive ? "#16a34a" : "#dc2626", color: "#fff", borderRadius: 999, padding: "3px 12px", fontSize: "var(--ns-text-xs)", fontWeight: 700 }}>
          {loading ? "—" : bandLabel}
        </span>
        <span style={{ fontSize: "var(--ns-text-xs)", color: T.slate, fontStyle: "italic" }}>
          {loading ? "" : tendency}
        </span>
      </div>
    </div>
  );
}

// ─── STYLE CARD (expandable) ──────────────────────────────────────────────────
function StyleCard({ def, score, bandLabel, isDominant }) {
  const [open, setOpen] = useState(isDominant);
  const color = STYLE_COLOR[def.code];
  const bandDesc = bandLabel && bandLabel !== "—" ? (BAND_DESCRIPTIONS[def.code]?.[bandLabel] ?? "") : "";
  return (
    <div
      className={`profile-card${isDominant ? " active" : ""}`}
      style={isDominant ? { borderColor: color } : {}}
    >
      {/* Header row: badge circle · label/tagline · score/band · toggle — click to expand/collapse */}
      <button
        onClick={() => setOpen(o => !o)}
        className="profile-header"
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0, marginBottom: open ? ".5rem" : 0 }}
      >
        <div className="profile-badge-circle" style={{ background: color }}>{def.code}</div>
        <div className="profile-meta">
          <strong style={{ color: T.ink }}>
            {def.label}
            {isDominant && <span className="your-tag" style={{ background: color }}>★ Dominant</span>}
          </strong>
          <em>{def.tagline}</em>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "clamp(1.2rem,2.4vw,1.7rem)", fontWeight: 800, color, lineHeight: 1 }}>
              {score ?? "—"}<span style={{ fontSize: ".75rem", fontWeight: 600, opacity: .55 }}>/25</span>
            </div>
            <span style={{ display: "inline-block", marginTop: ".35rem", background: color, color: "#fff", fontSize: ".68rem", fontWeight: 700, borderRadius: 999, padding: "3px 12px", whiteSpace: "nowrap" }}>
              {bandLabel}
            </span>
          </div>
          <span style={{ color, fontSize: "1.2rem", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>▾</span>
        </div>
      </button>

      {open && (
        <div>
          {/* Dimension badges */}
          <div style={{ marginBottom: ".5rem" }}>
            {[
              `Assertiveness: ${def.assertiveness}`,
              `Cooperativeness: ${def.cooperativeness}`,
              def.bargaining,
            ].map(b => (
              <span key={b} className="dim-badge"
                style={{ background: `${color}14`, border: `1px solid ${color}40`, color }}>
                {b}
              </span>
            ))}
          </div>

          <p>{def.overview}</p>
          {def.orgNote && <p style={{ color: T.slate, fontStyle: "italic", paddingBottom: ".5rem" }}>{def.orgNote}</p>}

          {/* Active band only */}
          {bandDesc && (
            <div className="band-section active-band" style={{ border: `1px solid ${color}40`, background: `${color}08`, marginBottom: ".75rem" }}>
              <div style={{ fontWeight: 700, fontSize: "var(--ns-text-xs)", color, marginBottom: ".25rem" }}>
                {bandLabel}
                <span style={{ marginLeft: 8, fontSize: ".68rem", background: color, color: "#fff", borderRadius: 999, padding: "1px 7px" }}>
                  Your band
                </span>
              </div>
              <p style={{ margin: 0, fontSize: "var(--ns-text-xs)", lineHeight: 1.6, color: T.ink }}>{bandDesc}</p>
            </div>
          )}

          <div>
            <strong style={{ fontSize: "var(--ns-text-xs)", color: T.navy, marginBottom: ".25rem" }}>Action steps:</strong>
            <ul style={{ marginTop: ".35rem", paddingLeft: "18px" }}>
              {def.toDo.map((item, i) => (
                <li key={i} style={{ marginBottom: ".3rem", color: T.ink, fontSize: "var(--ns-text-xs)", lineHeight: 1.6 }}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INSIGHT BUILDER ──────────────────────────────────────────────────────────
function buildNSInsight(data) {
  if (!data) return null;
  const dominant = data.dominant_style?.[0];
  const assertBand = data.assertiveness?.band_label ?? "—";
  const coopBand = data.cooperativeness?.band_label ?? "—";
  const map = {
    collaborating: `Your dominant style is Collaborating — the most powerful profile for Mutual Gain negotiations. With ${assertBand} Assertiveness and ${coopBand} Cooperativeness, you are equally driven to achieve results and to satisfy both parties. You naturally gravitate toward open communication and creative joint solutions.`,
    competing: `Your dominant style is Competing — you are results-oriented, self-confident, and assertive (${assertBand} Assertiveness, ${coopBand} Cooperativeness). You lean toward Distributive Bargaining. Channelled constructively, this drive is a powerful asset; watch for the risk of damaging long-term relationships by over-competing.`,
    compromising: `Your dominant style is Compromising — you seek the middle ground and workable mutual solutions. Moderate on both Assertiveness (${assertBand}) and Cooperativeness (${coopBand}), you are pragmatic and versatile. Be mindful that splitting the difference can leave value uncaptured that a collaborative approach would unlock.`,
    accommodating: `Your dominant style is Accommodating — you prioritise the relationship and the other party's needs. With ${assertBand} Assertiveness and ${coopBand} Cooperativeness, you lean toward Mutual Gain Bargaining. Be conscious of not consistently sacrificing your own outcomes in the process of maintaining harmony.`,
    avoiding: `Your dominant style is Avoiding — you tend to sidestep conflict rather than engage directly. With ${assertBand} Assertiveness and ${coopBand} Cooperativeness, unresolved issues can accumulate at a cost. Building the confidence and skills to engage proactively will significantly expand your negotiating effectiveness.`,
  };
  return map[dominant] ?? `Your dominant negotiating style is ${dominant ?? "unknown"}. Assertiveness: ${assertBand} · Cooperativeness: ${coopBand}.`;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function NSResult() {
  const location = useLocation();
  const navState = location.state || {};

  const [userInfo,        setUserInfo]        = useState(null);
  const [nsData,          setNsData]          = useState(null);
  const [scores,          setScores]          = useState(null);
  const [dominantStyles,  setDominantStyles]  = useState([]);
  const [insightText,     setInsightText]     = useState(null);
  const [submissionTime,  setSubmissionTime]  = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [pdfData,         setPdfData]         = useState(null);
  const [isGenerating,    setIsGenerating]    = useState(false);
  const generatingRef = useRef(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        // ── SCENARIO A: full data object ──
        if (navState.scores?.data) {
          hydrate(navState.scores);
          return;
        }

        // ── SCENARIO B: fields directly in navState ──
        if (navState.data && navState.respondent) {
          hydrate(navState);
          return;
        }

        // ── FALLBACK: re-fetch from API ──
        const email = navState.email ?? null;
        const phone = navState.phone ?? null;
        if (!email && !phone) {
          setError("No result data found. Please complete the survey first.");
          return;
        }

        const API_BASE = import.meta.env.VITE_API_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/negotiation`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error ?? `Server error ${res.status}`);
        }
        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response.");
        hydrate(json);

      } catch (e) {
        setError(e.message || "Failed to load report data. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    function hydrate(payload) {
      const respondent = payload.respondent ?? {};
      const d = payload.data ?? {};

      setUserInfo({
        name:       respondent.name       ?? navState.name       ?? null,
        email:      respondent.email      ?? navState.email      ?? null,
        phone:      respondent.phone      ?? navState.phone      ?? null,
        company:    respondent.company    ?? navState.company    ?? null,
        department: respondent.department ?? navState.department ?? null,
        submittedAt: respondent.submitted_at ?? null,
      });

      setNsData(d);

      // ── Capture submission_time for PDF ──
      const st = respondent.submitted_at ?? respondent.submission_time ?? d.submission_time ?? null;
      setSubmissionTime(st);

      const flat = {};
      Object.values(d.styles ?? {}).forEach(s => { flat[s.code] = s.score; });
      setScores(flat);

      const dominant = (d.dominant_style ?? []).map(k => {
        const def = NS_DEFINITIONS.find(nd => nd.key === k);
        return def?.code ?? k.toUpperCase().slice(0, 2);
      });
      setDominantStyles(dominant);
      setInsightText(buildNSInsight(d));
    }

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stylesForDisplay = NS_DEFINITIONS.map(def => {
    const apiStyle = nsData?.styles?.[def.key];
    const isDominant = dominantStyles.includes(def.code);
    return {
      def,
      score: apiStyle?.score ?? null,
      bandLabel: apiStyle?.band_label ?? getStyleBandLabel(def.code, apiStyle?.score),
      isDominant,
    };
  });

  // Sorted highest-score-first — used only for Pages 3 & 4
  const stylesForDisplaySorted = [...stylesForDisplay].sort(
    (a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity)
  );

  const assertiveness   = nsData?.assertiveness  ?? null;
  const cooperativeness = nsData?.cooperativeness ?? null;
  const dominantDef     = NS_DEFINITIONS.find(d => dominantStyles.includes(d.code));
  const dominantLabel   = dominantDef ? `${dominantDef.code} — ${dominantDef.label}` : "—";

  // ── Date string: use submission time if available, else today ──
  const dateStr = submissionTime
    ? formatDate(new Date(submissionTime))
    : loading ? "—" : formatDate(new Date());

  const handleDownloadPDF = useCallback(() => {
    if (loading || !scores || generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo:        userInfo || { name: "Participant" },
      nsData:          nsData  || {},
      scores:          scores  || {},
      dominantStyles:  dominantStyles || [],
      insightText:     insightText || "",
      submission_time: submissionTime,   // ← passes survey date to PDF
    });
  }, [loading, scores, userInfo, nsData, dominantStyles, insightText, submissionTime]);

  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  if (loading && !scores) return (
    <div style={{ padding: "2rem", textAlign: "center", fontFamily: "'Satoshi', sans-serif" }}>Loading Report…</div>
  );
  if (error && !scores) return (
    <div style={{ padding: "2rem", color: "red", fontFamily: "'Satoshi', sans-serif" }}>Error: {error}</div>
  );

  return (
    <>
      <div className="ns-report" id="ns-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          :root {
            --ns-text-xs:   clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
            --ns-text-sm:   clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
            --ns-text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
            --ns-text-lg:   clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
            --ns-text-xl:   clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
            --ns-space-4: 1rem; --ns-space-6: 1.5rem; --ns-space-8: 2rem;
            --ns-space-10: 2.5rem; --ns-space-12: 3rem; --ns-space-16: 4rem;
            --ns-radius: 20px; --ns-radius-sm: 12px;
            --ns-shadow: 0 18px 40px rgba(14,39,68,.10);
            --ns-font: 'Satoshi', sans-serif;

             /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
          }

          /* ── Root ── */
          .ns-report {
            font-family: var(--ns-font);
            background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
            color: #1D2733;
            line-height: 1.55;
          }
          .ns-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--ns-space-8) var(--ns-space-4) var(--ns-space-16);
          }

          /* ── Page shells ── */
          .ns-report .page {
            background: #ffffff;
            border: 1px solid #DCE5EF;
            border-radius: 28px;
            box-shadow: var(--ns-shadow);
            overflow: hidden;
            margin-bottom: var(--ns-space-8);
          }

          /* ── Cover ── */
          .ns-report .cover {
            min-height: 92vh;
            position: relative;
            background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                        linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
            color: white;
          }
          .ns-report .cover-inner {
            padding: var(--ns-space-16);
            display: flex; flex-direction: column;
            justify-content: space-between;
            min-height: 92vh;
          }
          .ns-report .brand {
            display: flex; align-items: center;
            justify-content: space-between;
            gap: var(--ns-space-4);
            position: relative; z-index: 2;
            padding-right: clamp(0px, 38vw, 200px);
          }
          .ns-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
           .ns-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
         .ns-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .ns-report .badge {
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem,1.5vw,0.85rem);
            white-space: nowrap; flex-shrink: 0;
          }
          .ns-report .cover-title { margin-top: var(--ns-space-16); }
          .ns-report .cover-title h2 {
            margin: 0 0 var(--ns-space-4);
            font-size: clamp(2.2rem, 5vw, 4.4rem);
            line-height: .96; color: #fff;
          }
          .ns-report .cover-title p {
            font-size: var(--ns-text-lg);
            max-width: 100%; color: rgba(255,255,255,.84); text-align: justify;
          }
          .ns-report .cover-grid {
            display: grid; grid-template-columns: repeat(3, 1fr);
            gap: var(--ns-space-4); margin-top: var(--ns-space-12);
          }
          .ns-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: 18px; padding: 1rem 1.1rem;
          }
          .ns-report .meta span   { display: block; font-size: var(--ns-text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
          .ns-report .meta strong { font-size: var(--ns-text-base); color: white; }
          .ns-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--ns-space-8); }
          .ns-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem;
          }
          .ns-report .footer-note {
            display: flex; justify-content: space-between; gap: var(--ns-space-4);
            color: rgba(255,255,255,.72); font-size: var(--ns-text-xs);
            margin-top: var(--ns-space-10);
          }

          /* ── Inner pages ── */
          .ns-report .page-inner { padding: var(--ns-space-10); }
          .ns-report .topbar {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: var(--ns-space-4); margin-bottom: var(--ns-space-8);
          }
          .ns-report .section-kicker {
            color: #c62828; font-size: var(--ns-text-xs);
            text-transform: uppercase; letter-spacing: .12em; font-weight: 700;
          }
          .ns-report .section-title { margin: .35rem 0 0; font-size: var(--ns-text-xl); color: #0f2744; }
          .ns-report .pill {
            display: inline-flex; align-items: center; gap: .45rem;
            padding: .5rem .85rem; border-radius: 999px;
            font-size: var(--ns-text-xs); font-weight: 700;
            text-transform: uppercase; letter-spacing: .06em; white-space: nowrap;
          }
          .ns-report .pill-red   { background: #fef2f2; color: #c62828; }
          .ns-report .pill-slate { background: #f1f5f9; color: #5B677A; }
          .ns-report .pill-green { background: #f0fdf4; color: #16a34a; }

          /* ── Cards ── */
          .ns-report .card {
            background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
            border: 1px solid #DCE5EF; border-radius: var(--ns-radius);
            padding: var(--ns-space-6);
          }
          .ns-report .card h3 { margin: 0 0 .75rem; font-size: var(--ns-text-lg); color: #0f2744; }
          .ns-report .lead { color: #334155; font-size: var(--ns-text-base); margin: 0; text-align: justify; }

          /* ── Sub-panel ── */
          .ns-report .sub-panel {
            background: #fbfdff; border: 1px solid #DCE5EF;
            border-radius: 16px; padding: 1.25rem;
          }
          .ns-report .sub-panel h4 { margin: 0 0 .65rem; font-size: var(--ns-text-base); color: #0f2744; }
          .ns-report .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

          /* ── Score bars ── */
          .ns-report .bar-row { display: grid; grid-template-columns: 175px 1fr 60px; gap: 1rem; align-items: center; margin-bottom: .8rem; }
          .ns-report .bar-label { font-size: var(--ns-text-sm); font-weight: 700; color: #0f2744; }
          .ns-report .bar-track { height: 22px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
          .ns-report .bar-fill  { height: 100%; border-radius: 999px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .ns-report .bar-val   { text-align: right; font-size: var(--ns-text-sm); font-weight: 700; font-variant-numeric: tabular-nums; }

          /* ── Score tiles ── */
          .ns-report .score-tile-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
          .ns-report .score-tile {
            border-radius: 14px; border: 1px solid #DCE5EF;
            padding: .7rem .85rem;
            background: linear-gradient(180deg,#fff,#f8fbff); min-width: 0; text-align: center;
          }
          .ns-report .score-tile-label { font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: #5B677A; font-weight: 700; margin-bottom: 2px; }
          .ns-report .score-tile-value { font-size: 1.7rem; font-weight: 800; line-height: 1.1; }
          .ns-report .score-tile-band  { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-top: 2px; }

          /* ── Style profile cards ── */
          .ns-report .profile-card { border: 1px solid #DCE5EF; border-radius: 16px; padding: clamp(.85rem,2vw,1rem); margin-bottom: .75rem; background: #fff; }
          .ns-report .profile-card.active { box-shadow: 0 0 0 2px rgba(168,13,44,.18); }
          .ns-report .profile-header { display: flex; align-items: center; gap: .85rem; margin-bottom: .5rem; flex-wrap: wrap; }
          .ns-report .profile-badge-circle {
            width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
            display: flex; align-items: center; justify-content: center;
            color: #fff; font-weight: 800; font-size: .85rem; letter-spacing: .02em;
          }
          .ns-report .profile-meta { min-width: 0; }
          .ns-report .profile-meta strong { display: block; font-size: var(--ns-text-sm); color: #1D2733; }
          .ns-report .profile-meta em { font-style: italic; font-size: var(--ns-text-xs); color: #5B677A; }
          .ns-report .your-tag {
            display: inline-block; font-size: .68rem; font-weight: 700; letter-spacing: .03em;
            margin-left: .5rem; padding: 2px 10px; border-radius: 999px; color: #fff;
            vertical-align: middle; text-transform: uppercase;
          }
          .ns-report .dim-badge { border-radius: 999px; padding: 3px 10px; font-size: var(--ns-text-xs); font-weight: 600; display: inline-block; margin: .2rem .3rem .2rem 0; }
          .ns-report .profile-card p { margin: 0 0 .5rem; font-size: var(--ns-text-sm); color: #334155; line-height: 1.6; }
          .ns-report .band-section { border-radius: 10px; padding: .65rem .85rem; margin-bottom: .5rem; border: 1px solid #DCE5EF; background: #f8fbff; }
          .ns-report .band-section.active-band { background: #fffdf5; }

          /* ── Back cover ── */
          .ns-report .back-cover { color: white; }
          .ns-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; min-height: 56vh; }
          .ns-report .back-cover-header {
            display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
            gap: .5rem; padding: clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem);
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .ns-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem,4vw,3rem); color: #fff; }
          .ns-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(.85rem,2vw,1.2rem); text-align: justify; }
          .ns-report .contact-grid {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px,100%), 1fr));
            gap: clamp(.4rem,1.2vw,1rem); margin-top: var(--ns-space-8);
          }
          .ns-report .contact {
            background: rgba(168,16,38,.1); border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px,2vw,16px); padding: clamp(.6rem,1.5vw,.9rem); min-width: 0;
          }
          .ns-report .contact span   { display: block; color: rgba(255,255,255,.64); font-size: var(--ns-text-xs); margin-bottom: .3rem; }
          .ns-report .contact strong { font-size: clamp(.78rem,1.6vw,.92rem); word-break: break-word; }
          .ns-report .light { color: rgba(255,255,255,.68); }

          /* ── Download button ── */
          #ns-pdf-btn {
            position: fixed; bottom: 2rem; right: 2rem; z-index: 9999;
            display: inline-flex; align-items: center; gap: .6rem;
            padding: .75rem 1.4rem;
            background: linear-gradient(135deg, #F3AC26, #D79A1E);
            color: white; border: 1px solid #F3AC26; border-radius: 999px;
            box-shadow: 0 8px 24px rgba(198,40,40,.35), 0 0 14px rgba(243,173,38,.45);
            cursor: pointer; font-family: var(--ns-font); font-weight: 700; font-size: 1rem;
            transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          }
          #ns-pdf-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(198,40,40,.45); }
          #ns-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #ns-pdf-btn:disabled { opacity: .72; cursor: not-allowed; }
          #ns-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,.35); border-top-color: white;
            border-radius: 50%; animation: ns-spin .7s linear infinite; flex-shrink: 0;
          }

          /* ── Animations ── */
          @keyframes ns-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
          @keyframes ns-fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          @keyframes ns-spin    { to{transform:rotate(360deg)} }
          .ns-fade-up { animation: ns-fadeUp .55s ease both; }

          /* ── Print ── */
          @media print {
            .ns-report { background: white; }
            .ns-report .report { max-width: none; padding: 0; }
            .ns-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; }
            #ns-pdf-btn { display: none !important; }
          }

          /* ── Tablet (≤ 768px) ── */
          @media (max-width: 768px) {
            .ns-report .report { padding: 1.25rem .75rem 3rem; }
            .ns-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
             
            
            
            .ns-report .cover { min-height: 88vh; }
            .ns-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
            .ns-report .cover-title { margin-top: 2.5rem; }
            .ns-report .cover-title h2 { font-size: clamp(1.8rem, 5vw, 2.8rem); }
            .ns-report .cover-grid { gap: .75rem; margin-top: 1.5rem; }
            .ns-report .page-inner { padding: 1.75rem; }
            .ns-report .topbar { flex-wrap: wrap; gap: .5rem; }
            .ns-report .pill { font-size: 10px; padding: .35rem .6rem; }
            .ns-report .two-col { grid-template-columns: 1fr; }
            .ns-report .score-tile-grid { grid-template-columns: repeat(3, 1fr); }
            .ns-report .bar-row { grid-template-columns: 120px 1fr 44px; gap: .6rem; }
            .ns-report .bar-track { height: 18px; }
            .ns-report .contact-grid { grid-template-columns: repeat(auto-fit, minmax(min(140px,100%),1fr)); }
          }

          /* ── Mobile (≤ 480px) ── */
          @media (max-width: 480px) {
            .ns-report .report { padding: .75rem .5rem 2.5rem; }
            .ns-report .page { border-radius: 16px; margin-bottom: .9rem; }
            .ns-report .cover { min-height: 100svh; }
            .ns-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
            
            

            .ns-report .cover-title { margin-top: 1.5rem; }
            .ns-report .cover-title h2 { font-size: clamp(1.5rem, 7vw, 2rem); line-height: 1.08; }
            .ns-report .cover-title p { font-size: .875rem; }
            .ns-report .cover-grid { grid-template-columns: repeat(auto-fit, minmax(min(130px,100%),1fr)); gap: .5rem; margin-top: 1.1rem; }
            .ns-report .meta { padding: .6rem .75rem; }
            .ns-report .cover-badges { margin-top: .9rem; gap: .4rem; }
            .ns-report .cover-badge { font-size: .7rem; padding: .3rem .55rem; }
            .ns-report .footer-note { flex-direction: column; gap: .2rem; font-size: .68rem; margin-top: 1rem; }
            .ns-report .page-inner { padding: 1rem; }
            .ns-report .topbar { flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
            .ns-report .section-title { font-size: 1.25rem; }
            .ns-report .pill { font-size: 9px; padding: .25rem .5rem; }
            .ns-report .card { padding: .875rem; border-radius: 14px; }
            .ns-report .card h3 { font-size: .95rem; margin-bottom: .45rem; }
            .ns-report .lead { font-size: .84rem; }
            .ns-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .ns-report .bar-label { font-size: .77rem; font-weight: 600; }
            .ns-report .bar-track { height: 14px; }
            .ns-report .bar-val { font-size: .77rem; text-align: right; }
            .ns-report .score-tile-grid { grid-template-columns: repeat(3, 1fr); }
            .ns-report .contact-grid { grid-template-columns: 1fr; gap: .5rem; margin-top: 1rem; }
            .ns-report .closing h2 { font-size: clamp(1.45rem,5.5vw,2rem); }
            .ns-report .closing p { font-size: .88rem; }
          }

          /* ── Tiny (≤ 390px) ── */
          @media (max-width: 390px) {
            .ns-report .report { padding: .5rem .35rem 2rem; }
            .ns-report .page { border-radius: 12px; margin-bottom: .75rem; }
            
            
            
            .ns-report .cover { min-height: 100svh; }
            .ns-report .cover-inner { padding: 1rem; min-height: 100svh; }
            .ns-report .brand { padding-right: clamp(0px,44vw,160px); }
            .ns-report .badge { font-size: .55rem; padding: .25rem .5rem; }
            .ns-report .cover-title { margin-top: 1rem; }
            .ns-report .cover-title h2 { font-size: clamp(1.35rem,6.5vw,1.7rem); line-height: 1.1; }
            .ns-report .cover-title p { font-size: .78rem; }
            .ns-report .cover-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .9rem; }
            .ns-report .meta { padding: .5rem .65rem; border-radius: 12px; }
            .ns-report .meta strong { font-size: .82rem; }
            .ns-report .cover-badges { margin-top: .75rem; gap: .3rem; }
            .ns-report .cover-badge { font-size: .65rem; padding: .25rem .45rem; }
            .ns-report .footer-note { flex-direction: column; gap: .15rem; font-size: .62rem; margin-top: .75rem; }
            .ns-report .page-inner { padding: .85rem; }
            .ns-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .ns-report .contact-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .75rem; }
          }
        `}</style>

        {/* ── Floating PDF Button ── */}
        <button id="ns-pdf-btn" onClick={handleDownloadPDF} disabled={isGenerating || loading || !scores} aria-label="Download PDF Report">
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel — SVG white wedge + Acumen logo */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                <div className="cover-title">
                  <h2>Negotiating <br></br>Styles Assessment</h2>
                  <p>
                    Based on Holley, Jennings &amp; Wolters (8th ed.).Measures your orientation
                    across five negotiating styles on two underlying dimensions — Assertiveness and
                    Cooperativeness — which together predict your tendency toward Distributive or
                    Mutual Gain Bargaining.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : (userInfo?.name || "—")}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                  <div className="meta"><span>Dominant Style</span><strong>{loading ? "—" : dominantLabel}</strong></div>
                </div>

                {/* <div className="cover-grid" style={{ marginTop: "1rem" }}>
                  <div className="meta"><span>Instrument</span><strong>Negotiating Styles (NS)</strong></div>
                  <div className="meta"><span>Report Type</span><strong>Individual Negotiating Profile</strong></div>
                  <div className="meta"><span>Score Range</span><strong>5 – 25 per style</strong></div>
                </div> */}

                {userInfo?.company && (
                  <div style={{ marginTop: "1rem", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 14, padding: ".9rem 1rem", display: "inline-flex", gap: "1.5rem", flexWrap: "wrap" }}>
                    <div><span style={{ fontSize: "var(--ns-text-xs)", color: "rgba(255,255,255,.65)" }}>Organisation</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.company}</strong></div>
                    {userInfo.department && <div><span style={{ fontSize: "var(--ns-text-xs)", color: "rgba(255,255,255,.65)" }}>Department</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.department}</strong></div>}
                  </div>
                )}

                <div className="cover-badges">
                  {["Executive Dashboard", "Pentagon Profile", "Five Negotiating Styles", "A–C Graph"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
                <div>Based on Holley, Jennings &amp; Wolters, 8th ed.</div>
                <div>Scored across five styles: CO · CL · CM · AC · AV</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════════════════ */}
          <section className="page ns-fade-up">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="pill pill-red">Dominant: {loading ? "—" : dominantLabel}</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.5rem" }}>
                The Negotiating Styles Self-Assessment (Holley, Jennings &amp; Wolters, 8th ed.) measures your orientation across five negotiating styles on two underlying dimensions — Assertiveness and Cooperativeness — which together predict your tendency toward Distributive or Mutual Gain Bargaining.
              </p>

              <div className="two-col" style={{ marginBottom: "1.5rem" }}>
                {/* Left — Pentagon + score tiles */}
                <div className="sub-panel">
                  <h4>Style Profile — Pentagon</h4>
                  <RadarChart scores={scores} dominantStyles={dominantStyles} loading={loading} />
                  <div className="score-tile-grid" style={{ marginTop: ".85rem" }}>
                    {stylesForDisplay.map(({ def, score, bandLabel, isDominant }) => (
                      <div key={def.code} className="score-tile" style={isDominant ? { border: `2px solid ${STYLE_COLOR[def.code]}`, background: `linear-gradient(180deg,${STYLE_COLOR[def.code]}10,#fff)` } : {}}>
                        <div className="score-tile-label" style={{ color: STYLE_COLOR[def.code] }}>{def.code}</div>
                        {loading
                          ? <Skeleton h={34} r={6} />
                          : <div className="score-tile-value" style={{ color: STYLE_COLOR[def.code] }}>{score ?? "—"}</div>
                        }
                        {!loading && <div className="score-tile-band" style={{ color: STYLE_COLOR[def.code] }}>{bandLabel}</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right — bars + insight */}
                <div className="sub-panel">
                  <h4>Score Breakdown</h4>
                  <p style={{ color: T.slate, fontSize: "var(--ns-text-xs)", margin: "0 0 .75rem" }}>
                    Each style is scored out of 25 (5 questions × 5-point scale). Band thresholds are derived from a national student sample.
                  </p>
                  {stylesForDisplay.map(({ def, score, isDominant }) => {
                    const pct = Math.min(100, ((score ?? 0) / 25) * 100);
                    const color = STYLE_COLOR[def.code];
                    return (
                      <div key={def.code} className="bar-row">
                        <div className={`bar-label${isDominant ? " dominant" : ""}`}
                        style={isDominant ? { color: STYLE_COLOR[def.code] } : {}}>{def.code} — {def.label}</div>
                        <div className="bar-track">
                          <div className="bar-fill" style={{
                            width: loading ? "0%" : `${pct}%`,
                            background:isDominant ? color : T.slate
                          }} />
                        </div>
                        <span className="bar-val" style={{ color: isDominant ? color : T.slate }}>{loading ? "—" : (score ?? "—")}</span>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: ".75rem" }}>
                    <Callout>
                      <strong>Evaluation insight: </strong>
                      {loading ? <Skeleton h={16} w="90%" r={4} /> : insightText}
                    </Callout>
                  </div>
                </div>
              </div>

              {/* Assertiveness & Cooperativeness Indexes */}
              <div>
                <div style={{ fontWeight: 700, fontSize: "var(--ns-text-base)", color: "#0f2744", marginBottom: ".75rem" }}>
                  Assertiveness &amp; Cooperativeness Indexes
                </div>
                <p className="lead" style={{ marginBottom: "1rem" }}>
                  These two composite indexes reveal your underlying orientation. Assertiveness drives toward Distributive Bargaining (win-lose); Cooperativeness drives toward Mutual Gain Bargaining (joint problem-solving).
                </p>
                <div className="two-col">
                  <IndexCard
                    title="Assertiveness Index"
                    index={assertiveness?.index}
                    bandLabel={assertiveness?.band_label}
                    formula="(Competing + Collaborating) − (Avoiding + Accommodating)"
                    tendency={assertiveness?.bargaining_tendency}
                    loading={loading}
                  />
                  <IndexCard
                    title="Cooperativeness Index"
                    index={cooperativeness?.index}
                    bandLabel={cooperativeness?.band_label}
                    formula="(Collaborating + Accommodating) − (Competing + Avoiding)"
                    tendency={cooperativeness?.bargaining_tendency}
                    loading={loading}
                  />
                </div>

                {!loading && (assertiveness || cooperativeness) && (
                  <div style={{ marginTop: "1rem" }}>
                    <Callout>
                      <strong>What this means: </strong>
                      {assertiveness?.index >= 5
                        ? "You are strongly assertive — you drive toward results and are comfortable leading negotiations. Pair this with cooperativeness to unlock Mutual Gain outcomes."
                        : assertiveness?.index >= 1
                          ? "You are moderately to highly assertive — you advocate for your position while remaining open to others. This is a balanced, effective profile."
                          : assertiveness?.index >= -2
                            ? "You are moderate to low on assertiveness — you may benefit from building confidence to advocate more directly for your interests in negotiations."
                            : "You are low on assertiveness — negotiation situations may feel uncomfortable. Developing assertiveness skills will significantly expand your effectiveness."}
                      {" "}
                      {cooperativeness?.index >= 9
                        ? "Your cooperativeness is high — you are strongly oriented toward joint solutions and relationship preservation."
                        : cooperativeness?.index >= 5
                          ? "Your cooperativeness is moderate to high — you value the other party's interests alongside your own."
                          : cooperativeness?.index >= 2
                            ? "Your cooperativeness is moderate to low — you may benefit from greater focus on the other party's underlying interests."
                            : "Your cooperativeness is low — consider practising perspective-taking and interest-based negotiation techniques."}
                    </Callout>
                  </div>
                )}

                {/* Definitions — sourced from the Negotiating Styles Self-Assessment document */}
                <div className="two-col" style={{ marginTop: "1rem" }}>
                  <div className="sub-panel">
                    <h4>What is Assertiveness?</h4>
                    <p style={{ color: T.slate, fontSize: "var(--ns-text-xs)", margin: 0, textAlign: "justify" }}>
                      Distributive Bargaining approaches negotiation as a win-lose exercise where the gains of one party must come at the expense of the other party. The sole focus of the negotiator is to maximize his/her own outcomes. In the negotiation literature, this orientation is referred to as Assertiveness.
                    </p>
                  </div>
                  <div className="sub-panel">
                    <h4>What is Cooperativeness?</h4>
                    <p style={{ color: T.slate, fontSize: "var(--ns-text-xs)", margin: 0, textAlign: "justify" }}>
                      Mutual Gain Bargaining approaches negotiation as a mutual problem-solving exercise. Relying on open communication, trust, and mutual respect, negotiators focus on fulfilling the mutual interests of both parties. In the negotiation literature, this orientation is referred to as Cooperativeness.
                    </p>
                  </div>
                </div>
                <p style={{ color: T.slate, fontSize: "var(--ns-text-xs)", fontStyle: "italic", marginTop: ".75rem" }}>
                  Negotiators that exhibit Assertiveness tendencies are more likely to engage in Distributive Bargaining behavior, while negotiators that are high in Cooperativeness are more likely to use a Mutual Gain Bargaining approach.
                </p>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — A–C GRAPH
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Assertiveness &amp; Cooperativeness Graph</h2>
                </div>
                <div className="pill pill-slate">Distributive vs Mutual Gain</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                This graph visually maps your negotiation orientation using your Assertiveness and Cooperativeness indexes. Your plotted position reflects whether your style aligns more closely with Competing, Collaborating, Avoiding, Accommodating, or Compromising behaviour.
              </p>

              <div style={{ background: "#fbfdff", border: "1px solid #DCE5EF", borderRadius: 18, padding: "clamp(.5rem, 2vw, 1.25rem)", marginBottom: "1.25rem" }}>
                <TwoDGraphSVG
                  assertivenessIndex={assertiveness?.index}
                  cooperativenessIndex={cooperativeness?.index}
                />
              </div>

              <div className="two-col">
                <div className="card">
                  <h3>Reading Your Position</h3>
                  <p className="lead">
                    The X-axis represents your Cooperativeness index (left = low, right = high). The Y-axis represents your Assertiveness index (down = low, up = high). The centre area marks the Compromising zone — the intersection of moderate assertiveness and cooperativeness.
                  </p>
                </div>
                <div className="card">
                  <h3>Your Indexes</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem", marginTop: ".4rem" }}>
                    {[
                      { label: "Assertiveness", obj: assertiveness },
                      { label: "Cooperativeness", obj: cooperativeness },
                    ].map(({ label, obj }) => {
                      const idx = obj?.index ?? null;
                      const isPos = (idx ?? 0) >= 0;
                      return (
                        <div key={label} style={{ padding: ".75rem", borderRadius: 12, border: `1px solid ${isPos ? "#86efac" : "#fca5a5"}`, background: isPos ? "#f0fff4" : "#fff0f0" }}>
                          <div style={{ fontSize: "var(--ns-text-xs)", color: T.slate, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em" }}>{label}</div>
                          <div style={{ fontSize: "1.8rem", fontWeight: 700, color: isPos ? "#16a34a" : "#dc2626", lineHeight: 1.1, marginTop: ".2rem" }}>
                            {idx != null ? (idx > 0 ? `+${idx}` : idx) : "—"}
                          </div>
                          <div style={{ fontSize: "var(--ns-text-xs)", fontWeight: 700, color: isPos ? "#16a34a" : "#dc2626", marginTop: ".3rem" }}>
                            {obj?.band_label ?? "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {error && (
                <div style={{ background: "#fff0f0", border: "1px solid #f5c6c6", borderRadius: 12, padding: "14px 18px", color: "#9b1c1c", fontSize: "var(--ns-text-sm)", marginTop: "1rem" }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </section>
          {/* ══════════════════════════════════════════════════════
              PAGE 4 — FIVE NEGOTIATING STYLES (AC · AV)
          ══════════════════════════════════════════════════════ */}
          {/* <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">The Five Negotiating Styles</h2>
                </div>
                <div className="pill pill-slate">CO · CL · CM · AC · AV</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Each style reflects a distinct combination of Assertiveness and Cooperativeness. Your dominant style is highlighted below.
              </p>

              <div style={{ display: "grid", gap: ".75rem" }}>
                {stylesForDisplaySorted.slice(3).map(({ def, score, bandLabel, isDominant }) => (
                  <StyleCard key={def.code} def={def} score={score} bandLabel={bandLabel} isDominant={isDominant} />
                ))}
              </div>
            </div>
          </section> */}

          {/* ══════════════════════════════════════════════════════
              PAGE 5 — FIVE NEGOTIATING STYLES (CO · CL · CM)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">The Five Negotiating Styles</h2>
                </div>
                <div className="pill pill-slate">CO · CL · CM · AC · AV</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Each style reflects a distinct combination of Assertiveness and Cooperativeness. Your dominant style is highlighted below.
              </p>

              <div style={{ display: "grid", gap: ".75rem" }}>
                {stylesForDisplaySorted.slice(0, 6).map(({ def, score, bandLabel, isDominant }) => (
                  <StyleCard key={def.code} def={def} score={score} bandLabel={bandLabel} isDominant={isDominant} />
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight: "56vh",
            }}>
              <div className="back-cover-header">
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>Acumen Business Catalyst</div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing" style={{ padding: "0.1rem" }}>
                    <h2>Developing Your Negotiating Effectiveness</h2>
                    <p>
                      Negotiating style is a dynamic capability, not a fixed trait. Awareness of your dominant orientation — and the conditions under which it serves or limits you — is the foundation of deliberate development. Acumen Business Catalyst uses assessments like this to turn behavioural data into focused conversations that build lasting negotiating competence.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Negotiation Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)", textAlign: "justify" }}>
                      For this participant, the most effective negotiating approach should draw on their dominant style as a natural starting point, while deliberately expanding into the styles that are underused. Each of the five styles has its place — the goal is not to change who you are, but to develop the versatility to match your approach to the situation, relationship, and stakes involved.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership · Culture · Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Individual Negotiating Style Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Negotiating Styles Self-Assessment framework. Based on Holley, Jennings &amp; Wolters, 8th ed. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator ── */}
      {pdfData && (
        <HiddenNSReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}